import React, {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import { Formik, Form, getIn } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { selectUser } from "../../redux/modules/userSlice";
import { BankDetailsAPI } from "../api";
import useDraft from "../../hooks/useDraft";
import { MediaPreviewDialog, MediaViewActions } from "../Shared/MediaPreview";
import {
  applySectionDraftData,
  pickSectionDraftData,
} from "../../drafts/draftHelpers";
import {
  JOINEE_FORM_COLORS,
  JOINEE_FORM_FONT,
} from "../../config/joineeFormConstants";
import signaturePlaceholder from "../../Assests/Images/joinee/form/signature-placeholder.png";
import {
  FormSectionTitle,
  FormTextField,
  FormTwoCol,
  FormCheckboxField,
} from "./JoineeFormFields";

import { checkInputRule, validateDraft, notifyFormInvalid } from "../../utils/inputRules";

var DRAFT_SECTION = "D";
var CHEQUE_MAX_BYTES = 1000 * 1024;
var ICICI_IFSC_REGEX = /^ICIC0[A-Z0-9]{6}$/;
var ALLOWED_CHEQUE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "application/pdf",
];

function errMsg(formik, path) {
  var touched = getIn(formik.touched, path);
  var error = getIn(formik.errors, path);
  return touched && error ? error : "";
}

function emptyBankDetails(userId) {
  return {
    userId: userId || "",
    isIcici: true,
    accountNumber: "",
    branch: "",
    ifscCode: "",
    passbook: "",
    passbook_filename: "",
  };
}

function safeFileName(name) {
  return String(name || "cheque")
    .replace(/[\\/<>:"|?*\x00-\x1F]/g, "_")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 120);
}

function digitsOnly(value) {
  return String(value || "").replace(/\D/g, "");
}

function normalizeIfsc(value) {
  var raw = String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
  if (!raw) {
    return "";
  }
  if (raw.indexOf("ICIC") === 0) {
    return raw.slice(0, 11);
  }
  return ("ICIC" + raw).slice(0, 11);
}

function ifscSuffix(full) {
  var normalized = normalizeIfsc(full);
  if (normalized.indexOf("ICIC") === 0) {
    return normalized.slice(4);
  }
  return normalized.slice(0, 7);
}

function isPdfCheque(passbook, fileName) {
  var name = String(fileName || "").toLowerCase();
  if (name.slice(-4) === ".pdf") {
    return true;
  }
  return String(passbook || "").indexOf("data:application/pdf") === 0;
}

function isAllowedChequeFile(file) {
  if (!file) {
    return false;
  }
  var type = String(file.type || "").toLowerCase();
  var name = String(file.name || "").toLowerCase();
  if (ALLOWED_CHEQUE_TYPES.indexOf(type) !== -1) {
    return true;
  }
  return (
    /\.(jpe?g|png|webp|pdf)$/.test(name) &&
    (type === "" || type === "application/octet-stream")
  );
}

function buildValidationSchema() {
  return Yup.object({
    isIcici: Yup.boolean(),
    accountNumber: Yup.string().when("isIcici", {
      is: true,
      then: function (schema) {
        return schema
          .trim()
          .required("Account number is required")
          .test("icici-account", "", function (val) {
            var message = checkInputRule("iciciAccountNumber", val);
            return message ? this.createError({ message: message }) : true;
          });
      },
      otherwise: function (schema) {
        return schema.nullable();
      },
    }),
    branch: Yup.string().when("isIcici", {
      is: true,
      then: function (schema) {
        return schema
          .trim()
          .required("Branch is required")
          .test("branch-format", "", function (val) {
            var message = checkInputRule("bankText", val, "Branch");
            return message ? this.createError({ message: message }) : true;
          });
      },
      otherwise: function (schema) {
        return schema.nullable();
      },
    }),
    ifscCode: Yup.string().when("isIcici", {
      is: true,
      then: function (schema) {
        return schema
          .trim()
          .required("IFSC code is required")
          .test(
            "icici-ifsc",
            "Enter a valid ICICI IFSC code",
            function (val) {
              return ICICI_IFSC_REGEX.test(normalizeIfsc(val));
            }
          );
      },
      otherwise: function (schema) {
        return schema.nullable();
      },
    }),
    passbook: Yup.string().when("isIcici", {
      is: true,
      then: function (schema) {
        return schema.required("Please upload a copy of your cheque");
      },
      otherwise: function (schema) {
        return schema.nullable();
      },
    }),
  });
}

function setBusy(onBusyChange, value) {
  if (typeof onBusyChange === "function") {
    onBusyChange(value);
  }
}

function scrollToFirstBankError() {
  setTimeout(function () {
    var target = document.querySelector('.Mui-error, [aria-invalid="true"]');
    if (target && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, 50);
}

function BankDetailsForm(props) {
  var formikRef = props.formikRef;
  var changeTab = props.changeTab;
  var handleButtonClick = props.handleButtonClick;
  var onBusyChange = props.onBusyChange;
  var hideInternalActions = Boolean(props.hideInternalActions);
  var readOnly = Boolean(props.readOnly);

  var reduxUser = useSelector(selectUser);
  var params = useParams();
  var id = params.id ? params.id : "";
  var user = reduxUser && reduxUser.user ? reduxUser.user : {};
  var isAdmin = Boolean(user.isAdmin);
  var userId = isAdmin ? id || props.id : user._id;

  var draftApi = useDraft("newJoinee", DRAFT_SECTION);
  var loadDraft = draftApi.loadDraft;
  var saveDraft = draftApi.saveDraft;
  var deleteDraft = draftApi.deleteDraft;

  var [initialValues, setInitialValues] = useState(emptyBankDetails(userId));
  var [formKey, setFormKey] = useState(0);
  var [loading, setLoading] = useState(true);
  var [chequePreviewOpen, setChequePreviewOpen] = useState(false);
  var formikInstanceRef = useRef(null);
  var chequeInputRef = useRef(null);

  useEffect(
    function () {
      var isActive = true;

      function finish(row) {
        if (!isActive) {
          return;
        }
        var base = emptyBankDetails(userId);
        var next = Object.assign({}, base, row || {}, { userId: userId });
        next.ifscCode = normalizeIfsc(next.ifscCode);
        next.accountNumber = digitsOnly(next.accountNumber).slice(0, 18);
        if (typeof next.isIcici !== "boolean") {
          next.isIcici = true;
        }
        setInitialValues(next);
        setFormKey(function (k) {
          return k + 1;
        });
        setLoading(false);
      }

      setLoading(true);

      function loadApi() {
        BankDetailsAPI.getBankDetails(userId)
          .then(function (res) {
            if (!isActive) {
              return;
            }
            finish(res && res.data ? res.data : emptyBankDetails(userId));
          })
          .catch(function (err) {
            console.log(err);
            if (isActive) {
              setLoading(false);
              toast.error("Unable to load bank details");
            }
          });
      }

      if (!isAdmin) {
        loadDraft()
          .then(function (loaded) {
            if (!isActive) {
              return;
            }
            var draftData = loaded && loaded.data ? loaded.data : loaded;
            var applied = applySectionDraftData(
              "newJoinee",
              DRAFT_SECTION,
              { bankDetails: emptyBankDetails(userId) },
              draftData
            );
            if (
              applied &&
              applied.bankDetails &&
              typeof applied.bankDetails === "object" &&
              (applied.bankDetails.accountNumber ||
                applied.bankDetails.passbook ||
                applied.bankDetails.isIcici === false ||
                applied.bankDetails._id)
            ) {
              finish(applied.bankDetails);
              return;
            }
            loadApi();
          })
          .catch(function () {
            loadApi();
          });
      } else {
        loadApi();
      }

      return function () {
        isActive = false;
      };
    },
    [userId, isAdmin, loadDraft]
  );

  function payloadFromValues(values) {
    var hasAccount = Boolean(values.isIcici);
    if (!hasAccount) {
      return {
        _id: values._id,
        userId: userId,
        isIcici: false,
        accountNumber: "",
        branch: "",
        ifscCode: "",
        passbook: "",
        passbook_filename: "",
      };
    }
    return {
      _id: values._id,
      userId: userId,
      isIcici: true,
      accountNumber: digitsOnly(values.accountNumber).slice(0, 18),
      branch: String(values.branch || "").trim(),
      ifscCode: normalizeIfsc(values.ifscCode),
      passbook: values.passbook || "",
      passbook_filename: safeFileName(values.passbook_filename),
    };
  }

  function handleSaveDraft(formik) {
    if (isAdmin) {
      return Promise.resolve();
    }
    return validateDraft(formik, buildValidationSchema()).then(function (ok) {
      return ok ? persistDraft(formik) : null;
    });
  }

  function persistDraft(formik) {
    var wrapped = pickSectionDraftData("newJoinee", DRAFT_SECTION, {
      bankDetails: payloadFromValues(formik.values),
    });
    setBusy(onBusyChange, "draft");
    return saveDraft(wrapped)
      .then(function (saved) {
        setBusy(onBusyChange, false);
        if (saved && saved.skipped) {
          toast.info("Nothing to save as draft");
          return saved;
        }
        if (saved) {
          toast.success("Draft saved");
          return saved;
        }
        toast.error("Unable to save draft");
        return null;
      })
      .catch(function () {
        setBusy(onBusyChange, false);
        toast.error("Unable to save draft");
        return null;
      });
  }

  function handleSaveAndNext(values, formikHelpers) {
    var payload = payloadFromValues(values);
    setBusy(onBusyChange, "next");
    if (typeof handleButtonClick === "function") {
      handleButtonClick("sectionD");
    }

    var request = payload._id
      ? BankDetailsAPI.updateBankDetails(payload._id, payload)
      : BankDetailsAPI.createBankDetails(payload);

    request
      .then(function () {
        var after = isAdmin ? Promise.resolve(true) : deleteDraft();
        return after.then(function () {
          setBusy(onBusyChange, false);
          if (formikHelpers && formikHelpers.setSubmitting) {
            formikHelpers.setSubmitting(false);
          }
          toast.success(
            payload.isIcici
              ? "Bank details saved"
              : "Skipped — no ICICI account"
          );
          if (typeof changeTab === "function") {
            changeTab(4);
          }
        });
      })
      .catch(function (err) {
        console.log(err);
        setBusy(onBusyChange, false);
        if (formikHelpers && formikHelpers.setSubmitting) {
          formikHelpers.setSubmitting(false);
        }
        toast.error("Unable to save bank details");
      });
  }

  useImperativeHandle(
    formikRef,
    function () {
      return {
        submitDraft: function () {
          var formik = formikInstanceRef.current;
          if (!formik || readOnly) {
            return Promise.resolve();
          }
          return handleSaveDraft(formik);
        },
        submitNext: function () {
          var formik = formikInstanceRef.current;
          if (!formik || readOnly) {
            return Promise.resolve();
          }
          return formik.validateForm().then(function (errors) {
            if (errors && Object.keys(errors).length) {
              notifyFormInvalid(errors);
              formik.setTouched(
                {
                  accountNumber: true,
                  branch: true,
                  ifscCode: true,
                  passbook: true,
                },
                true
              );
              scrollToFirstBankError();
              return;
            }
            return formik.submitForm();
          });
        },
        getFormik: function () {
          return formikInstanceRef.current;
        },
      };
    },
    [userId, isAdmin, onBusyChange, changeTab, handleButtonClick, readOnly]
  );

  if (loading) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography
          sx={{
            fontFamily: JOINEE_FORM_FONT,
            fontSize: 14,
            fontWeight: 400,
            color: JOINEE_FORM_COLORS.placeholder,
          }}
        >
          Loading bank details…
        </Typography>
      </Box>
    );
  }

  return (
    <Formik
      key={formKey}
      initialValues={initialValues}
      enableReinitialize
      validationSchema={buildValidationSchema()}
      onSubmit={handleSaveAndNext}
    >
      {function (formik) {
        formikInstanceRef.current = formik;
        var values = formik.values;
        var hasAccount = Boolean(values.isIcici);
        var chequeIsPdf = isPdfCheque(
          values.passbook,
          values.passbook_filename
        );

        function onSkipChange(e) {
          var skip = Boolean(e.target.checked);
          if (skip) {
            formik.setValues(
              Object.assign({}, values, {
                isIcici: false,
                accountNumber: "",
                branch: "",
                ifscCode: "",
                passbook: "",
                passbook_filename: "",
              })
            );
            formik.setErrors({});
            formik.setTouched({});
          } else {
            formik.setFieldValue("isIcici", true);
          }
        }

        function onAccountChange(e) {
          var next = digitsOnly(e.target.value).slice(0, 12);
          formik.setFieldValue("accountNumber", next);
        }

        function onIfscChange(e) {
          var suffix = String(e.target.value || "")
            .toUpperCase()
            .replace(/[^A-Z0-9]/g, "")
            .slice(0, 7);
          formik.setFieldValue("ifscCode", normalizeIfsc(suffix));
        }

        function onChequeFile(e) {
          var file =
            e.target.files && e.target.files[0] ? e.target.files[0] : null;
          if (!file) {
            return;
          }
          if (!isAllowedChequeFile(file)) {
            toast.error("Upload a JPG, PNG, WEBP, or PDF file", {
              hideProgressBar: true,
              closeOnClick: true,
            });
            e.target.value = "";
            return;
          }
          if (file.size > CHEQUE_MAX_BYTES) {
            toast.error("File size exceeds the 1000 KB limit", {
              hideProgressBar: true,
              closeOnClick: true,
            });
            e.target.value = "";
            return;
          }
          var reader = new FileReader();
          reader.onload = function (ev) {
            formik.setFieldValue("passbook", ev.target.result);
            formik.setFieldValue(
              "passbook_filename",
              safeFileName(file.name)
            );
            formik.setFieldTouched("passbook", true, false);
          };
          reader.readAsDataURL(file);
          e.target.value = "";
        }

        function clearCheque() {
          formik.setFieldValue("passbook", "");
          formik.setFieldValue("passbook_filename", "");
          formik.setFieldTouched("passbook", true, false);
        }

        return (
          <Form noValidate>
            <Box
              component="fieldset"
              disabled={readOnly}
              sx={{
                border: 0,
                m: 0,
                p: 0,
                minWidth: 0,
                pointerEvents: readOnly ? "none" : "auto",
              }}
            >
            <FormSectionTitle
              action={
                <FormCheckboxField
                  name="skipIcici"
                  checked={!hasAccount}
                  onChange={onSkipChange}
                  label="I don’t have ICICI Bank Account"
                />
              }
            >
              ICICI Bank Details
            </FormSectionTitle>

            <FormTwoCol>
              <FormTextField
                id="bank-account"
                name="accountNumber"
                label="Account Number"
                placeholder="e.g. 123456789012"
                hint="ICICI savings account numbers are 12 digits."
                required={hasAccount}
                value={values.accountNumber}
                onChange={onAccountChange}
                onBlur={formik.handleBlur}
                error={errMsg(formik, "accountNumber")}
                disabled={!hasAccount}
                inputProps={{
                  inputMode: "numeric",
                  autoComplete: "off",
                  maxLength: 12,
                }}
              />
              <FormTextField
                id="bank-name"
                name="bankName"
                label="Bank"
                value="ICICI BANK"
                disabled
              />
            </FormTwoCol>

            <FormTwoCol>
              <FormTextField
                id="bank-branch"
                rule="bankText"
                name="branch"
                label="Branch"
                placeholder="e.g. Baner, Pune"
                required={hasAccount}
                value={values.branch}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={errMsg(formik, "branch")}
                disabled={!hasAccount}
                inputProps={{ maxLength: 60, autoComplete: "off" }}
              />
              <FormTextField
                id="bank-ifsc"
                name="ifscCode"
                label="IFSC Code"
                placeholder="e.g. 0001234"
                hint="ICICI IFSC is ICIC followed by 0 and 6 characters, e.g. ICIC0001234."
                required={hasAccount}
                value={ifscSuffix(values.ifscCode)}
                onChange={onIfscChange}
                onBlur={formik.handleBlur}
                error={errMsg(formik, "ifscCode")}
                disabled={!hasAccount}
                inputProps={{
                  maxLength: 7,
                  autoComplete: "off",
                  style: { textTransform: "uppercase" },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: JOINEE_FORM_FONT,
                          fontSize: 14,
                          fontWeight: 500,
                          color: "#171A1C",
                        }}
                      >
                        ICIC
                      </Typography>
                    </InputAdornment>
                  ),
                }}
              />
            </FormTwoCol>

            <FormSectionTitle>Upload a copy of your cheque</FormSectionTitle>
            <Box
              sx={{
                width: { xs: "100%", sm: 338 },
                maxWidth: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                mb: 2,
                opacity: hasAccount ? 1 : 0.5,
                pointerEvents: hasAccount ? "auto" : "none",
              }}
            >
              <Box
                onClick={function () {
                  if (values.passbook) {
                    setChequePreviewOpen(true);
                  }
                }}
                sx={{
                  width: "100%",
                  height: 149,
                  borderRadius: "6px",
                  bgcolor: "#EDEDED",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  cursor: values.passbook ? "zoom-in" : "default",
                  border: values.passbook
                    ? "1px solid " + JOINEE_FORM_COLORS.border
                    : "none",
                }}
              >
                {values.passbook && !chequeIsPdf ? (
                  <Box
                    component="img"
                    src={values.passbook}
                    alt="Cheque preview"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : values.passbook && chequeIsPdf ? (
                  <Typography
                    sx={{
                      fontFamily: JOINEE_FORM_FONT,
                      fontSize: 14,
                      fontWeight: 500,
                      color: "#171A1C",
                      px: 2,
                      textAlign: "center",
                    }}
                  >
                    PDF uploaded
                    <br />
                    <Box
                      component="span"
                      sx={{
                        fontSize: 12,
                        fontWeight: 400,
                        color: "#848F9A",
                      }}
                    >
                      Click to view
                    </Box>
                  </Typography>
                ) : (
                  <Box
                    component="img"
                    src={signaturePlaceholder}
                    alt=""
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                )}
              </Box>
              {values.passbook ? (
                <Typography
                  sx={{
                    fontFamily: JOINEE_FORM_FONT,
                    fontSize: 12,
                    fontWeight: 400,
                    color: "#848F9A",
                    mt: -0.5,
                  }}
                >
                  {values.passbook_filename
                    ? values.passbook_filename
                    : "Cheque uploaded"}{" "}
                  — click image to view
                </Typography>
              ) : null}
              {errMsg(formik, "passbook") ? (
                <Typography
                  sx={{
                    fontFamily: JOINEE_FORM_FONT,
                    fontSize: 12,
                    color: JOINEE_FORM_COLORS.error,
                  }}
                >
                  {errMsg(formik, "passbook")}
                </Typography>
              ) : null}
              {readOnly ? (
                <MediaViewActions
                  src={values.passbook}
                  fileName={values.passbook_filename}
                  fallbackName="cheque"
                  onView={function () {
                    setChequePreviewOpen(true);
                  }}
                />
              ) : (
              <>
              <input
                ref={chequeInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
                style={{ display: "none" }}
                onChange={onChequeFile}
                disabled={!hasAccount}
              />
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  type="button"
                  variant="outlined"
                  disabled={!hasAccount}
                  onClick={function () {
                    if (chequeInputRef.current) {
                      chequeInputRef.current.click();
                    }
                  }}
                  sx={{
                    flex: 1,
                    minWidth: 120,
                    fontFamily: JOINEE_FORM_FONT,
                    fontSize: 14,
                    fontWeight: 400,
                    textTransform: "none",
                    color: "#171A1C",
                    borderColor: JOINEE_FORM_COLORS.border,
                    borderRadius: "6px",
                    minHeight: 32,
                    bgcolor: JOINEE_FORM_COLORS.surface,
                    "&:hover": {
                      borderColor: JOINEE_FORM_COLORS.navy,
                      bgcolor: "#F5F7FF",
                    },
                  }}
                >
                  {values.passbook ? "Replace Cheque" : "Upload Cheque"}
                </Button>
                {values.passbook ? (
                  <Button
                    type="button"
                    variant="outlined"
                    disabled={!hasAccount}
                    onClick={function () {
                      setChequePreviewOpen(true);
                    }}
                    sx={{
                      fontFamily: JOINEE_FORM_FONT,
                      fontSize: 14,
                      fontWeight: 400,
                      textTransform: "none",
                      color: JOINEE_FORM_COLORS.primary,
                      borderColor: JOINEE_FORM_COLORS.primary,
                      borderRadius: "6px",
                      minHeight: 32,
                      px: 2,
                    }}
                  >
                    View
                  </Button>
                ) : null}
                {values.passbook ? (
                  <Button
                    type="button"
                    variant="outlined"
                    disabled={!hasAccount}
                    onClick={clearCheque}
                    sx={{
                      fontFamily: JOINEE_FORM_FONT,
                      fontSize: 14,
                      fontWeight: 400,
                      textTransform: "none",
                      color: JOINEE_FORM_COLORS.error,
                      borderColor: JOINEE_FORM_COLORS.border,
                      borderRadius: "6px",
                      minHeight: 32,
                      px: 2,
                    }}
                  >
                    Remove
                  </Button>
                ) : null}
              </Box>
              </>
              )}
            </Box>

            <MediaPreviewDialog
              open={chequePreviewOpen}
              onClose={function () {
                setChequePreviewOpen(false);
              }}
              src={values.passbook}
              fileName={values.passbook_filename}
              fallbackName="cheque"
              title="Copy of cheque"
            />

            </Box>

            {!hideInternalActions ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1.5,
                  mt: 2,
                }}
              >
                <Button
                  type="button"
                  variant="outlined"
                  onClick={function () {
                    handleSaveDraft(formik);
                  }}
                  sx={{
                    fontFamily: JOINEE_FORM_FONT,
                    textTransform: "none",
                    fontWeight: 400,
                  }}
                >
                  Save as Draft
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    fontFamily: JOINEE_FORM_FONT,
                    textTransform: "none",
                    fontWeight: 400,
                    bgcolor: JOINEE_FORM_COLORS.primary,
                  }}
                >
                  Save and next
                </Button>
              </Box>
            ) : null}
          </Form>
        );
      }}
    </Formik>
  );
}

export default BankDetailsForm;
